import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminLogin } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";

/**
 * Admin Portal — standalone sign-in page.
 *
 * This page is intentionally isolated:
 *   - NOT linked from any public navigation on www.rebuiltindia.co
 *   - Reachable ONLY via the admin subdomain (https://admin.rebuiltindia.co → /admin-portal)
 *     or by typing the URL directly (/admin-portal, /admin/login alias).
 *   - No signup, no customer links, no agent links, no back-to-home link.
 *
 * Security (enforced server-side):
 *   - /api/auth/admin-login is IP rate-limited: 5 failed attempts per 15 minutes.
 *   - Only JWTs with role ∈ {super_admin, ops_staff} can reach /api/admin/* endpoints.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null); // UNIX ms, set when backend returns 429

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in both email and password");
      return;
    }
    if (lockedUntil && Date.now() < lockedUntil) {
      const mins = Math.max(1, Math.ceil((lockedUntil - Date.now()) / 60000));
      toast.error(`Too many attempts. Try again in ${mins} minute${mins === 1 ? '' : 's'}.`);
      return;
    }
    setLoading(true);
    try {
      const res = await adminLogin(email, password);
      login(res.data.token, res.data.user, res.data.user.role);
      toast.success("Admin login successful");
      navigate("/admin/dashboard");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        // Backend locked this IP for 15 min. Save the deadline locally for friendlier UX.
        setLockedUntil(Date.now() + 15 * 60 * 1000);
        toast.error(detail || "Too many attempts. Try again in 15 minutes.");
      } else {
        toast.error(detail || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="admin-login-page">
      {/* Minimal header — NO navigation back to main site. Admin portal is a closed loop. */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-6 py-4">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <img src="/assets/logo-icon-blue.png" alt="Rebuilt" className="h-10 w-10" />
          <span
            className="text-lg font-black text-[#0A2540] tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}
          >
            REBUILT
          </span>
          <span className="ml-auto text-[9px] uppercase tracking-[0.25em] text-[#EF4444] font-bold">
            Admin
          </span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md backdrop-blur-xl bg-white/70 border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl p-10 fade-in">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#1e293b] flex items-center justify-center mx-auto mb-5 shadow-lg">
              <Shield className="h-8 w-8 text-white" strokeWidth={1.5} />
            </div>
            <h2
              className="text-3xl font-black text-[#0A2540] tracking-tight mb-2"
              style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}
            >
              Admin Portal
            </h2>
            <p className="text-sm text-[#4B5563]">Authorised personnel only</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-2 block">
                Email
              </Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@rebuiltindia.co"
                autoComplete="username"
                className="mt-2 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#0A2540]/20 focus:border-[#0A2540]"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-2 block">
                Password
              </Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                className="mt-2 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#0A2540]/20 focus:border-[#0A2540]"
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading || (lockedUntil && Date.now() < lockedUntil)}
              className="w-full bg-[#0A2540] hover:bg-[#1e293b] text-white py-4 rounded-full font-semibold transition-all shadow-sm hover:shadow hover:-translate-y-0.5 mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
            All access is logged.
          </p>
        </div>
      </div>
    </div>
  );
}
